export interface PvpSeason {
  /**
   * Niantic season number, e.g. 27. Null when the season came from the live
   * feed under a name this table does not know and is not the row right after
   * the last one here (lib/pvpSeasonProgress works the number out).
   */
  number: number | null;
  /** Display name, e.g. "Forever Forward" */
  name: string;
  /** Season start: epoch ms, 10:00 AM Pacific Time on the start date */
  startMs: number;
  /** Season end: epoch ms, 10:00 AM Pacific Time on the end date */
  endMs: number;
}

// FALLBACK + NUMBER LOOKUP, not the live source (2026-09-28). The Profile's
// PvP season card reads the current season from the events feed
// (/api/events/live, category "season", the LeekDuck row the admin events
// page adopts, hides or ends early), via lib/pvpSeasonProgress. This table is
// consulted only when the feed has no live season, and to put a number on a
// feed season by name. Appending a row is optional housekeeping, not what
// makes a new season appear: the card read "No active season" for three weeks
// in September 2026 because Season 28 was never appended here.
// Source: Leek Duck + Pokemon GO official announcements.
// PST (Nov-Mar) is UTC-8 so 10:00 AM PT is 18:00 UTC; PDT (Mar-Nov) is UTC-7
// so 10:00 AM PT is 17:00 UTC. Use the correct offset per season window.
export const PVP_SEASONS: PvpSeason[] = [
  {
    number: 25,
    name: "Precious Paths",
    // Dec 2 2025 10:00 AM PST (UTC-8)
    startMs: Date.UTC(2025, 11, 2, 18, 0, 0, 0),
    // Mar 3 2026 10:00 AM PST (UTC-8)
    endMs: Date.UTC(2026, 2, 3, 18, 0, 0, 0),
  },
  {
    number: 26,
    name: "Memories in Motion",
    // Mar 3 2026 10:00 AM PST (UTC-8)
    startMs: Date.UTC(2026, 2, 3, 18, 0, 0, 0),
    // Jun 2 2026 10:00 AM PDT (UTC-7)
    endMs: Date.UTC(2026, 5, 2, 17, 0, 0, 0),
  },
  {
    number: 27,
    name: "Forever Forward",
    // Jun 2 2026 10:00 AM PDT (UTC-7)
    startMs: Date.UTC(2026, 5, 2, 17, 0, 0, 0),
    // Sep 8 2026 10:00 AM PDT (UTC-7)
    endMs: Date.UTC(2026, 8, 8, 17, 0, 0, 0),
  },
  {
    number: 28,
    name: "Twilight Trails",
    // Sep 8 2026 10:00 AM PDT (UTC-7)
    startMs: Date.UTC(2026, 8, 8, 17, 0, 0, 0),
    // Dec 1 2026 10:00 AM PST (UTC-8)
    endMs: Date.UTC(2026, 11, 1, 18, 0, 0, 0),
  },
];

export interface PvpSeasonProgress {
  season: PvpSeason | null;
  /** Fraction 0..1, or null when no current season is found */
  fraction: number | null;
  /** Whole days remaining, or null when no current season is found */
  daysRemaining: number | null;
  /** Whole days total in the season, or null */
  daysTotal: number | null;
}

export const NO_PVP_SEASON: PvpSeasonProgress = Object.freeze({
  season: null,
  fraction: null,
  daysRemaining: null,
  daysTotal: null,
});

/** Progress through the hardcoded table only. Prefer lib/pvpSeasonProgress. */
export function getCurrentPvpSeason(now: number): PvpSeasonProgress {
  const season = PVP_SEASONS.find((s) => now >= s.startMs && now < s.endMs) ?? null;
  if (!season) return NO_PVP_SEASON;
  return pvpSeasonProgress(season, now);
}

/** Fraction / days maths for any season window, feed-sourced or hardcoded. */
export function pvpSeasonProgress(season: PvpSeason, now: number): PvpSeasonProgress {
  const total = season.endMs - season.startMs;
  const elapsed = now - season.startMs;
  const fraction = Math.max(0, Math.min(1, elapsed / total));
  const dayMs = 24 * 60 * 60 * 1000;
  const daysRemaining = Math.max(0, Math.ceil((season.endMs - now) / dayMs));
  const daysTotal = Math.round(total / dayMs);
  return { season, fraction, daysRemaining, daysTotal };
}
